import { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "universal-cookie";
import styled from "styled-components/macro";
import PageNotFound from "./PageNotFound";
import SendInvite from "./SendInvite";
import SettingsPanel from "./SettingsPanel";
import { CenterBox, MobileWarning, setMetaThemeColor, startUrl } from "./Utils";
import Bonus from "./components/Bonus";
import HelpMenu from "./components/HelpMenu";
import Subscription from "./models/Subscription";
import UserData from "./models/UserData";
import { useStableCallback } from "./hooks/useStableCallback";

import update from "react-addons-update";
import { Route, Switch, useLocation } from "react-router-dom";

import { omit } from "lodash";
import { useDebounce } from "use-debounce";
import Checkout from "./Checkout";
import CommunityManagerJobListing from "./components/CommunityManagerJobListing";
import MainFAQ from "./components/MainFAQ";
import Pricing from "./components/Pricing";
import Usage from "./components/Usage";
import { applyTheme, getThemeType, unapplyTheme } from "./lib/Theme";

import * as Sentry from "@sentry/react";
import { Redirect } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min.js";
import { DEFAULT_WRITE_MODEL, UNLIMITED_EXPERIMENT_VARIATIONS } from "../../src/shared/constants";
import { isMuse2AlphaWriteModel } from "./components/WriteSettings/muse2Alpha";
import Auth from "./Auth";
import PrefilledProjectDialog from "./components/PrefilledProjectDialog";
// import { UNLIMITED_EXPERIMENT_VARIATIONS } from "../../src/shared/constants";
// import Auth from "./Auth";
import AppHome from "./components/AppHome/index.js";
import Canvas from "./components/Canvas";
import DocumentRedirect from "./components/DocumentRedirect";
import EditPlugin from "./components/EditPlugin";
import { FeaturebaseSignin } from "./components/FeaturebaseChangelog/index.js";
import ImportScreen from "./components/ImportScreen/index.js";
import Menu from "./components/Menu";
import TaxModal from "./components/Modals/Tax/index.js";
import { V4TransitionModal } from "./components/Modals/V4TransitionModal/index.js";
import MyVoice from "./components/MyVoice/index.js";
import NewUserShadowPage from "./components/NewUserShadow/index.js";
import Plugin from "./components/Plugin";
import Plugins from "./components/Plugins";
import PluginsWaitlist from "./components/PluginsWaitlist";
import Project from "./components/Project";
import PurchasePacks from "./components/PurchasePacks";
import Reviser from "./components/Reviser";
import StoryChat from "./components/StoryChat";
import StoryEngineRedirect from "./components/StoryEngineRedirect";
import SurveyModal from "./components/SurveyConversational/surveyModal.js";
import TurnstileHost from "./components/TurnstileHost/index.js";
import useAppRedirectionUrl from "./hooks/useAppRedirectionUrl.js";
import useUserMetadata from "./hooks/useUserMetadata.js";
import useNovelImportAccess from "./hooks/useNovelImportAccess.js";
import { useAppStore } from "./stores/appStore";
import { useSudowriteStore } from "./stores/sudowriteStore";
import Questionnaire from "./components/Onboarding/Questionnaire";
import { MobileDeviceWarning } from "./components/MobileDeviceWarning/index.js";
import { isMobileOnly } from "react-device-detect";
import { requiresConferencePayment } from "../../src/shared/conferenceCampaign";
import StickyNoteManager from "./components/StickyTour/StickyNoteManager";
import StyleGuideBetaEnrollment from "./components/StyleGuideBetaEnrollment";
import { STYLE_GUIDE_BETA_ENROLLMENT_PATH, isStyleGuideBetaEnrollmentPath } from "../../src/shared/styleGuideBetaEnrollment";
import Muse2AlphaEnrollment from "./components/Muse2AlphaEnrollment";
import { MUSE2_ALPHA_ENROLLMENT_PATH, isMuse2AlphaEnrollmentPath } from "../../src/shared/muse2AlphaEnrollment";

const AppRedirection = ({ userData }) => {
  const redirectUrl = useAppRedirectionUrl({ userData });

  if (!redirectUrl) {
    return null;
  }

  return <Redirect to={redirectUrl} />;
};

const AppLoadContent = styled.div`
  min-height: 100vh;
  html.ios-viewport & {
    /* 100vh on iOS includes the area behind Safari's browser chrome. */
    min-height: var(--app-height, 100vh);
  }
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 420ms ease-out;
`;

function Sudowrite({
  user,
  showFront,
  hideFront,
  setNeedsUserCreation,
  isAppLoadingTransitionActive = false,
  onAppReadyChange,
  onAppDarkThemeChange,
}) {
  // This is source of truth for the state of the editor options: used to drive both the editor and userdata saving. If you need to change the state of the editor, change this

  // Store state variables
  const reset = useSudowriteStore((state) => state.reset);
  const editorOptions = useSudowriteStore((state) => state.editorOptions);
  const setEditorOptions = useSudowriteStore((state) => state.setEditorOptions);
  const userData = useSudowriteStore((state) => state.userData);
  const setUserData = useSudowriteStore((state) => state.setUserData);
  const subscription = useSudowriteStore((state) => state.subscription);
  const setSubscription = useSudowriteStore((state) => state.setSubscription);
  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);
  const setSubscriptionJSON = useSudowriteStore((state) => state.setSubscriptionJSON);
  const fetchCustomModels = useSudowriteStore((state) => state.fetchCustomModels);
  const settingsOpen = useSudowriteStore((state) => state.settingsOpen);
  const setSettingsOpen = useSudowriteStore((state) => state.setSettingsOpen);
  const demo = useSudowriteStore((state) => state.demo);
  const setDemo = useSudowriteStore((state) => state.setDemo);
  const needsImport = useSudowriteStore((state) => state.needsImport);
  const setNeedsImport = useSudowriteStore((state) => state.setNeedsImport);
  const hasImportedOrSkipped = useSudowriteStore((state) => state.hasImportedOrSkipped);
  const setHasImportedOrSkipped = useSudowriteStore((state) => state.setHasImportedOrSkipped);
  const helpOpen = useSudowriteStore((state) => state.helpOpen);
  const setHelpOpen = useSudowriteStore((state) => state.setHelpOpen);
  // Unlimited Experiment
  const unlimitedExperimentVariation = useSudowriteStore((state) => state.unlimitedExperimentVariation);
  const setIsInUnlimitedExperiment = useSudowriteStore((state) => state.setIsInUnlimitedExperiment);
  const setUnlimitedExperimentVariation = useSudowriteStore((state) => state.setUnlimitedExperimentVariation);

  const handleEndOnboardingTour = useCallback(async () => {
    if (userData?.data?.needsDemo) {
      const updatePromise = userData.update({ needsDemo: false });
      setDemo(false);
      await updatePromise;
    }
  }, [userData, setDemo]);

  // Local state variables

  const [debouncedEditorOptions] = useDebounce(editorOptions, 1000);
  const [totalDocuments, setTotalDocuments] = useState(null);
  const [needsFeedback, setNeedsFeedback] = useState(false);
  const updateNeedsFeedback = useStableCallback((enabled) => {
    userData.update({ needsFeedback: enabled });
    setNeedsFeedback(enabled);
  });
  const [hideEDUCards, setHideEDUCards] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const { userMetadata, isLegacyProjectFlowTreatment, isLoading: isUserMetadataLoading } = useUserMetadata();
  useNovelImportAccess({
    userId: subscription?.fetched && subscription.userId === user?.uid ? user.uid : null,
    hasAccessToAgenticNovelImport: subscription?.hasAccessToAgenticNovelImport,
  });
  const [showImportScreen, setShowImportScreen] = useState(false);
  const [prefilledProjectData, setPrefilledProjectData] = useState(null);
  const [showPrefilledDialog, setShowPrefilledDialog] = useState(false);
  const [hasValidPrefilledProject, setHasValidPrefilledProject] = useState(false);
  const [checkingPrefilledProject, setCheckingPrefilledProject] = useState(true);
  const [userFromPrefilledProject, setUserFromPrefilledProject] = useState(false);

  // Labs
  const [summaryEnabled, setSummaryEnabled] = useState(false);
  const [expandEnabled, setExpandEnabled] = useState(false);
  const [expandOptions, setExpandOptions] = useState(null);
  const [filterEnabled, setFilterEnabled] = useState(false);

  // Theme
  const [theme, setTheme] = useState(() => new Cookies().get("theme") || null);

  // TODO: Remove temporary SW2 setter after SW2.0 general release
  const setIsSW2 = useAppStore((state) => state.setIsSW2);

  // Re-enable this + backend route when/ if we do a new unlimited experiment
  useEffect(() => {
    if (user) {
      Auth.fetchWithIdToken("/api/unlimited-experiment", {}, null, {
        requestSource: "startup-unlimited-experiment",
        responseReadFailureRetries: 2,
        responseReadFailureRetryDelayMs: 250,
      }).then(({ variationId }) => {
        if (variationId) {
          setIsInUnlimitedExperiment(variationId !== "control");
          setUnlimitedExperimentVariation(variationId);
        }
      });
    }
  }, [user]);

  useEffect(() => {
    if (userData?.fetched && !userData?.data?.hasChangedWriteDefault && unlimitedExperimentVariation === UNLIMITED_EXPERIMENT_VARIATIONS.UNLIMITED_DEFAULT) {
      userData.update({ writeModel: "basic" });
    }
  }, [userData?.fetched, userData?.data?.hasChangedWriteDefault, unlimitedExperimentVariation]);

  // Phase 1 turtle models were removed when Phase 2 launched. Any saved
  // standalone alpha value falls back to ordinary Write.
  useEffect(() => {
    if (!subscription?.fetched) return;
    if (!isMuse2AlphaWriteModel(editorOptions?.writeModel)) return;
    const fallbackWriteModel =
      unlimitedExperimentVariation === UNLIMITED_EXPERIMENT_VARIATIONS.UNLIMITED_DEFAULT ? "basic" : DEFAULT_WRITE_MODEL;
    setEditorOptions(update(editorOptions, { $merge: { writeModel: fallbackWriteModel } }));
  }, [subscription?.fetched, editorOptions, unlimitedExperimentVariation, setEditorOptions]);

  // Reset the store on unmount
  useEffect(() => {
    return () => {
      reset();
      unapplyTheme();
    };
  }, []);

  // set the theme color
  useEffect(() => {
    setMetaThemeColor("#FFDCFE");
  });

  const location = useLocation();
  const joiningEnrollment = isMuse2AlphaEnrollmentPath(location.pathname) || isStyleGuideBetaEnrollmentPath(location.pathname);

  // Update growthbook on route changes
  useEffect(() => {
    if (window.growthbook) {
      window.growthbook.refresh();
    }
  }, [location]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    let prefilledCode = searchParams.get("prefilled");

    if (!prefilledCode) {
      prefilledCode = window.sessionStorage.getItem("prefilled_project_code");
    } else {
      window.sessionStorage.setItem("prefilled_project_code", prefilledCode);
    }

    if (userData && userData.fetched) {
      if (prefilledCode) {
        window.sessionStorage.removeItem("prefilled_project_code");

        Auth.fetchWithIdToken(`/api/proto-projects/${prefilledCode}`)
          .then((protoProject) => {
            if (protoProject && !protoProject.used) {
              setPrefilledProjectData({
                shortCode: prefilledCode,
                title: protoProject.title,
              });
              setShowPrefilledDialog(true);
              setHasValidPrefilledProject(true);

              if (userData.data.needsDemo || userData.data.needsImport) {
                userData.update({
                  needsDemo: false,
                  needsImport: false,
                  hasImportedOrSkipped: true,
                  genrePrimary: "fiction",
                });
              }

              setUserFromPrefilledProject(true);

              Auth.fetchWithIdToken("/api/user-metadata/survey", {
                survey: {
                  skipped: true,
                  source: "prefilled_project",
                },
              }).catch((err) => {
                Sentry.captureException(err);
              });
            }
            setCheckingPrefilledProject(false);
          })
          .catch((error) => {
            Sentry.captureException(error);
            window.sessionStorage.removeItem("prefilled_project_code");
            setCheckingPrefilledProject(false);
          });
      } else {
        setCheckingPrefilledProject(false);
      }
    }
  }, [userData, location.search]);

  const [sleekToken, setSleekToken] = useState(null); // sleek SSO token
  const sleekTag = useRef(null);

  useEffect(() => {
    try {
      if (!window.$sleek) {
        window.SLEEK_COOKIE_DOMAIN = "editor.sudowrite.com";
        window.$sleek = [];
        window.SLEEK_PRODUCT_ID = 985904264;
        const d = document;
        sleekTag.current = d.createElement("script");
        sleekTag.current.src = "https://client.sleekplan.com/sdk/e.js";
        sleekTag.current.async = 1;
        d.getElementsByTagName("head")[0].appendChild(sleekTag.current);
      }
    } catch (e) {
      // do nothing
    }
  }, []);

  // Set initial data about the user
  useEffect(() => {
    if (user?.uid) {
      setUserData(new UserData(user.uid));
      setSubscription(new Subscription(user.uid));
      fetchCustomModels();
    } else {
      setUserData(null);
    }
  }, [user?.uid]);

  // Once userData is set, fetch it
  useEffect(() => {
    // propagates state to sudowrite components from UserData
    function propagate(ud) {
      // A previous account's Firestore listener can finish after switching users.
      if (useSudowriteStore.getState().userData !== ud) return;
      setEditorOptions(ud.data);

      const prefilledCode = window.sessionStorage.getItem("prefilled_project_code");
      if (prefilledCode && (ud.data.needsDemo || ud.data.needsImport)) {
        // Don't set demo/import flags if we have a prefilled project
        setDemo(false);
        setNeedsImport(false);
        setHasImportedOrSkipped(true);
      } else {
        setDemo(ud.data.needsDemo);
        setNeedsImport(ud.data.needsImport);
        setHasImportedOrSkipped(ud.data.hasImportedOrSkipped);
      }

      setNeedsFeedback(ud.data.needsFeedback);
      setHideEDUCards(ud.data.hideWhatsNewCards); //legacy variable name

      setTotalDocuments(ud.data.documentsOrder.length);

      // Labs
      setSummaryEnabled(ud.data.summaryEnabled);
      setExpandEnabled(ud.data.expandEnabled);
      setExpandOptions(ud.data.expandOptions);
      setFilterEnabled(ud.data.filterEnabled);

      setTheme(ud.data.theme);

      if (ud.data.theme) {
        // remember theme in cookie
        const cookies = new Cookies();
        cookies.set("theme", ud.data.theme, {
          maxAge: 60 * 60 * 24 * 365 * 4,
          path: "/",
        });
      }
    }

    if (userData && !userData.fetched) {
      userData
        .fetchAndListen((ud) => {
          // If there are future transforms that need to happen on UserData, move this logic
          // to the UserData model and add a general pre-processing transform function
          ud.data.font = ud.data?.font === "Fira Mono" ? "Quattro" : ud.data?.font;
          propagate(ud);
        })
        .then((ud) => {
          if (!ud) {
            console.log("UserData doesn't exist for user");
            Sentry.captureMessage("UserData doesn't exist for user", {
              tags: {
                userId: user?.uid,
              },
            });
            setLoadError(true);
            setNeedsUserCreation(true); // kick this back up to the <Main> Component to create the user
          } else {
            propagate(ud);
          }
        });
    }
  }, [userData]);

  // Theme
  useEffect(() => {
    applyTheme(theme);

    if (theme === "sorbet-auto") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => applyTheme(theme);
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [theme]);

  // Fetch subscription once set
  useEffect(() => {
    if (subscription && !subscription.fetched) {
      subscription
        .fetchAndListen((s) => {
          if (s.data.deletedAt) {
            // if user becomes deleted, refresh the page
            window.location.reload();
          }
          setSubscriptionJSON(s.toJSON());
        })
        .then((exists) => {
          if (!exists) {
            // the associated subscription for the user doesn't exist
            // which means that the user is likely not in the beta
            console.log("Subscription doesn't exist for user");
            Sentry.captureMessage("Subscription doesn't exist for user", {
              tags: {
                userId: user?.uid,
              },
            });
            setLoadError(true);
            setNeedsUserCreation(true); // kick this back up to the <Main> Component to create the user
          } else {
            setSubscriptionJSON(subscription.toJSON());
          }
        });
    }
  }, [subscription]);

  useEffect(() => {
    // TODO: Remove after 2.0 general release
    if (subscription && subscription.fetched) {
      if (!subscription.effectiveAdminForFeatures) {
        setIsSW2(subscription.data?.features?.sw2);
      }
    }
  }, [subscription?.fetched]);

  // When editorOptions are updated, then we save it via userData.
  // Promo cards are hidden through userData directly, and echoing this copy back
  // would restore cards hidden in another tab since the last snapshot.
  useEffect(() => {
    if (userData && userData.fetched) {
      userData.update(omit(editorOptions, "disabledPromoCards"));
    }
  }, [debouncedEditorOptions]);

  // when we link to /app?settings=open, open settings panel
  const history = useHistory();
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("settings") === "open") {
      setSettingsOpen(true);
      // Remove the query parameter from the URL without triggering a reload
      history.replace({ pathname: location.pathname });
    }
  }, [location.search]);

  let ready = userData && userData.fetched && subscription && subscription.fetched && subscriptionJSON && userMetadata !== undefined;
  const isDarkTheme = getThemeType(theme) === "dark";

  useEffect(() => {
    onAppReadyChange?.(!!ready || loadError);
  }, [loadError, onAppReadyChange, ready]);

  useEffect(() => {
    onAppDarkThemeChange?.(isDarkTheme);
  }, [isDarkTheme, onAppDarkThemeChange]);

  if (loadError) {
    return (
      <section id="container">
        <div id="app">
          <CenterBox noTitle={true}>
            <h1>There was a problem loading the app</h1>
            <p>
              We've been notified of this. In the meantime, try refreshing the page. If that doesn't work, try <a href="/logout">logging out</a> and logging in again.
            </p>
          </CenterBox>
        </div>
      </section>
    );
  }

  // On the initial signup, userMetadata is usually null which ends up giving us
  // the correct value for surveyCompleted, but it feels sort of buggy.
  const surveyCompleted = ready && (!!userData?.data?.genrePrimary || !!userMetadata?.survey?.completedAt);
  const requiresSubscriptionFirst = ready && subscription.features?.requiresSubscriptionFirst && !subscription.hasPaid;
  const conferencePaymentRequired = ready && requiresConferencePayment(subscription.data);
  const shouldRedirectNewMobileSignupToPlans =
    ready && isMobileOnly && demo && !surveyCompleted && !hasValidPrefilledProject;
  const shouldRedirectToPlans = requiresSubscriptionFirst || conferencePaymentRequired || shouldRedirectNewMobileSignupToPlans;
  // userMetadata should have been loaded or we would not have gotten here
  return (
    <section id="container">
      {ready && <TurnstileHost />}
      <div id="app">
        {ready && (
          <AppLoadContent $visible={!isAppLoadingTransitionActive}>
            {!joiningEnrollment && !conferencePaymentRequired && <V4TransitionModal />}
            {prefilledProjectData && !conferencePaymentRequired && (
              <PrefilledProjectDialog
                open={showPrefilledDialog}
                onOpenChange={setShowPrefilledDialog}
                shortCode={prefilledProjectData.shortCode}
                projectTitle={prefilledProjectData.title}
                onProjectCreated={() => {
                  setHasValidPrefilledProject(false);
                  setPrefilledProjectData(null);
                }}
                onCancel={() => {
                  setHasValidPrefilledProject(false);
                  setPrefilledProjectData(null);
                  setUserFromPrefilledProject(false);
                  setCheckingPrefilledProject(false);
                  if (userData && userData.data.needsDemo) {
                    setDemo(true);
                  }
                  if (userData && userData.data.needsImport) {
                    setNeedsImport(true);
                  }
                }}
              />
            )}
            <Switch>
          {conferencePaymentRequired && !/^\/app\/(plans|checkout|billing)(\/|$)/.test(location.pathname) && (
            <Route><Redirect to="/app/plans" /></Route>
          )}
          {/* Headerless routes go here */}
          <Route exact path={STYLE_GUIDE_BETA_ENROLLMENT_PATH}>
            <StyleGuideBetaEnrollment user={user} />
          </Route>
          <Route exact path={MUSE2_ALPHA_ENROLLMENT_PATH}>
            <Muse2AlphaEnrollment user={user} />
          </Route>

          <Route
            exact
            path="/"
          >
            <AppRedirection userData={userData} />
          </Route>

          <Route
            exact
            path="/app/survey-shadow"
          >
            <NewUserShadowPage />
          </Route>

          <Route
            exact
            path={["/app", "/app/folder/:folderId"]}
          >
            <MobileWarning />

            {shouldRedirectToPlans && <Redirect to="/app/plans" />}
            {!shouldRedirectToPlans && !isMobileOnly && (demo || isLegacyProjectFlowTreatment) && !surveyCompleted && !hasValidPrefilledProject && <Redirect to="/app/onboarding" />}

            {(!!needsImport || (!demo && showImportScreen && !hasImportedOrSkipped)) && !hasValidPrefilledProject && (
              <ImportScreen
                userData={userData}
                setShow={setShowImportScreen}
                user={user}
              />
            )}
            {!showImportScreen && !shouldRedirectToPlans && (
              <AppHome
                subscription={subscriptionJSON}
                deleted={false}
                settingsPanel={
                  <SettingsPanel
                    demo={demo}
                    setOpen={setSettingsOpen}
                    open={settingsOpen}
                    setDemo={(d) => {
                      setDemo(d);
                    }}
                    subscription={subscriptionJSON}
                    font={editorOptions.font}
                    fontSize={editorOptions.fontSize}
                    textIndent={editorOptions.textIndent}
                    lineHeight={editorOptions.lineHeight}
                    paragraphSpacing={editorOptions.paragraphSpacing}
                    showWordCount={editorOptions.showWordCount}
                    theme={editorOptions.theme}
                    showCreditsUsedToast={editorOptions.showCreditsUsedToast}
                    onSubmit={(data) => {
                      setEditorOptions(update(editorOptions, { $merge: data }));
                    }}
                    v2={true}
                  />
                }
                helpMenu={
                  <HelpMenu
                    sleekToken={sleekToken}
                    setOpen={setHelpOpen}
                    open={helpOpen}
                    subscription={subscriptionJSON}
                    showFront={showFront}
                    v2={true}
                  />
                }
                user={user}
                userData={userData}
                userMetadata={userMetadata}
              />
            )}
          </Route>

          <Route
            exact
            path={["/app/projects/deleted", "/app/projects/deleted/:folderId"]}
          >
            <AppHome
              deleted={true}
              settingsPanel={
                <SettingsPanel
                  demo={demo}
                  setOpen={setSettingsOpen}
                  open={settingsOpen}
                  setDemo={(d) => {
                    setDemo(d);
                  }}
                  subscription={subscriptionJSON}
                  font={editorOptions.font}
                  fontSize={editorOptions.fontSize}
                  textIndent={editorOptions.textIndent}
                  lineHeight={editorOptions.lineHeight}
                  paragraphSpacing={editorOptions.paragraphSpacing}
                  showWordCount={editorOptions.showWordCount}
                  theme={editorOptions.theme}
                  showCreditsUsedToast={editorOptions.showCreditsUsedToast}
                  onSubmit={(data) => {
                    setEditorOptions(update(editorOptions, { $merge: data }));
                  }}
                  v2={true}
                />
              }
              helpMenu={
                <HelpMenu
                  sleekToken={sleekToken}
                  setOpen={setHelpOpen}
                  open={helpOpen}
                  subscription={subscriptionJSON}
                  showFront={showFront}
                  v2={true}
                />
              }
              user={user}
              userData={userData}
              userMetadata={userMetadata}
            />
          </Route>

          <Route path="/app/plans">
            {isMobileOnly && shouldRedirectToPlans && !conferencePaymentRequired ? (
              <MobileDeviceWarning />
            ) : (
              <Pricing
                showFront={showFront}
                hideFront={hideFront}
              />
            )}
          </Route>

          <Route path="/app/purchase-packs">
            <PurchasePacks
              subscription={subscriptionJSON}
              nextUrl={startUrl() + "/app"}
              close={() => {}}
            />
          </Route>

          <Route path="/app/FAQ">
            <MainFAQ />
          </Route>
          <Route path="/app/jobs-community">
            <CommunityManagerJobListing />
          </Route>
          <Route path="/app/checkout">
            <Checkout
              user={user}
              subscription={subscriptionJSON}
            />
          </Route>

          <Route path="/app/story-engine">
            <StoryEngineRedirect
              user={user}
              subscription={subscriptionJSON}
            />
          </Route>

          <Route
            exact
            path="/app/project/:projectId/canvas"
          >
            <Canvas
              user={user}
              subscription={subscription}
              userData={userData}
              setSettingsOpen={setSettingsOpen}
              settingsOpen={settingsOpen}
              demo={demo}
              setDemo={setDemo}
              subscriptionJSON={subscriptionJSON}
              editorOptions={editorOptions}
              setEditorOptions={setEditorOptions}
              sleekToken={sleekToken}
              setHelpOpen={setHelpOpen}
              helpOpen={helpOpen}
              hasInteractedWithCanvas={userData.data.hasInteractedWithCanvas}
              setHasInteractedWithCanvas={(val) => {
                userData.update({ hasInteractedWithCanvas: true });
              }}
            />
          </Route>

          <Route path="/app/onboarding">
            {shouldRedirectNewMobileSignupToPlans ? (
              <Redirect to="/app/plans" />
            ) : (
              <Questionnaire
                setHasImportedOrSkipped={setHasImportedOrSkipped}
                userData={userData}
                surveyCompleted={surveyCompleted}
                subscription={subscription}
              />
            )}
          </Route>

          {/* <Route
            exact
            path="/app/project/:projectId/book-engine"
          >
            <BookEngine
              user={user}
              subscription={subscription}
              userData={userData}
            />
          </Route> */}

          <Route
            exact
            path="/app/project/:projectId/story-chat"
          >
            <StoryChat
              user={user}
              subscription={subscription}
              userData={userData}
            />
          </Route>

          {/* List of routes that require the header */}
          <Route
            exact
            path={[
              "/app/project/:id",
              "/app/plugins",
              "/app/plugins/*",
              "/app/my-voice",
              "/app/my-plugin/*",
              "/app/plugin/*",
              "/app/document/:id",
              "/app/project/:projectId/document/:documentId/summary",
              "/app/invite",
              "/app/usage",
              "/app/bonus/:code",
              "/app/project/:projectId/reviser",
            ]}
          >
            <Menu
              subscription={subscription}
              userData={userData}
              setSettingsOpen={setSettingsOpen}
              settingsOpen={settingsOpen}
              demo={demo}
              setDemo={setDemo}
              subscriptionJSON={subscriptionJSON}
              editorOptions={editorOptions}
              setEditorOptions={setEditorOptions}
              sleekToken={sleekToken}
              setHelpOpen={setHelpOpen}
              helpOpen={helpOpen}
              showFront={showFront}
              hideHiring={location.pathname === "/app/my-voice" || location.pathname.startsWith("/app/my-plugin/")}
              hideNew={location.pathname === "/app/my-voice" || location.pathname.startsWith("/app/my-plugin/")}
              hideLabs={location.pathname === "/app/my-voice" || location.pathname.startsWith("/app/my-plugin/")}
              hideBackButton={location.pathname.startsWith("/app/my-plugin/")}
              isLoading={location.pathname.startsWith("/app/project/") || !ready}
            />

            <div id="main">
              <MobileWarning />
              <>
                {ready && subscriptionJSON.activeSubscription && (
                  <Route
                    exact
                    path="/app/invite"
                  >
                    <SendInvite />
                  </Route>
                )}

                <Route
                  exact
                  path="/app/project/:projectId/reviser"
                >
                  <Reviser
                    user={user}
                    subscription={subscription}
                    userData={userData}
                    setSettingsOpen={setSettingsOpen}
                    settingsOpen={settingsOpen}
                    demo={demo}
                    setDemo={setDemo}
                    subscriptionJSON={subscriptionJSON}
                    editorOptions={editorOptions}
                    setEditorOptions={setEditorOptions}
                    sleekToken={sleekToken}
                    setHelpOpen={setHelpOpen}
                    helpOpen={helpOpen}
                  />
                </Route>

                {!subscription.hasAccessToPlugins && (
                  <Route
                    exact
                    path={["/app/plugins", "/app/plugins/*", "/app/plugin/*"]}
                  >
                    <PluginsWaitlist user={user} />
                  </Route>
                )}

                <Route
                  exact
                  path="/app/my-voice"
                >
                  <MyVoice user={user} />
                </Route>

                {subscription.hasAccessToPlugins && (
                  <>
                    <Route
                      exact
                      path={[
                        "/app/plugins", //
                        "/app/plugins/search/:query",
                        "/app/plugins/search/",
                        "/app/plugins/category/:categorySlug",
                      ]}
                    >
                      <Plugins user={user} />
                    </Route>

                    <Switch>
                      <Route
                        exact
                        path="/app/my-plugin/edit/:id"
                      >
                        <EditPlugin
                          user={user}
                          subscription={subscriptionJSON}
                        />
                      </Route>

                      <Route
                        exact
                        path={[
                          "/app/plugin/:id/:slug",
                          "/app/plugin/:id", //
                        ]}
                      >
                        <Plugin
                          subscription={subscriptionJSON}
                          user={user}
                        />
                      </Route>
                    </Switch>

                    <Route
                      exact
                      path="/app/my-plugin/new"
                    >
                      <EditPlugin user={user} />
                    </Route>
                  </>
                )}

                <Route
                  exact
                  path="/app/project/:id"
                >
                  <Project
                    user={user}
                    setHideEDUCards={setHideEDUCards}
                    userData={userData}
                    setSettingsOpen={setSettingsOpen}
                    setHelpOpen={setHelpOpen}
                    theme={theme}
                    totalDocuments={totalDocuments}
                    subscription={subscriptionJSON}
                    customizableWormhole={subscriptionJSON.data.customizableWormhole}
                    needsFeedback={needsFeedback}
                    hideEDUCards={hideEDUCards}
                    updateNeedsFeedback={updateNeedsFeedback}
                    updateHideEDUCards={(hidden) => {
                      userData.update({ hideWhatsNewCards: hidden }); //legacy variable name
                    }}
                    editorOptions={editorOptions}
                    setEditorOptions={(data) => {
                      setEditorOptions(update(editorOptions, { $merge: data }));
                    }}
                    summaryEnabled={summaryEnabled}
                    expandEnabled={expandEnabled}
                    filterEnabled={filterEnabled}
                    updateSizes={(sizes) => {
                      if (userData.fetched) {
                        userData.update({
                          mainSize: sizes.main,
                          rightSize: sizes.right,
                        });
                      }
                    }}
                    wormholeOutputLength={editorOptions.wormholeOutputLength}
                    wormholeOutputCards={editorOptions.wormholeOutputCards}
                    creativitySliderPosition={editorOptions.creativitySliderPosition}
                    showFront={showFront}
                    writeModel={editorOptions.writeModel}
                  />
                </Route>

                <Route
                  exact
                  path="/app/document/:id"
                >
                  <DocumentRedirect />
                </Route>
                <Route
                  exact
                  path="/app/usage"
                >
                  <Usage />
                </Route>

                <Route
                  exact
                  path="/app/bonus/:code"
                >
                  <Bonus subscription={subscriptionJSON} />
                </Route>
              </>
            </div>
          </Route>

          <Route
            exact
            path="/app/featurebase/login"
          >
            <FeaturebaseSignin />
          </Route>

          {/* 404 Page */}
          <Route>
            <PageNotFound />
          </Route>
            </Switch>
              {!joiningEnrollment && !conferencePaymentRequired && !demo && !isLegacyProjectFlowTreatment && !hasValidPrefilledProject && !checkingPrefilledProject && !userFromPrefilledProject && <SurveyModal />}
              {!joiningEnrollment && <TaxModal />}
              {!joiningEnrollment && <StickyNoteManager
              needsOnboardingTour={demo}
              isLegacyProjectFlowTreatment={isLegacyProjectFlowTreatment}
              userData={userData}
              onEndOnboardingTour={handleEndOnboardingTour}
              userMetadata={userMetadata}
            />}
          </AppLoadContent>
        )}
      </div>
    </section>
  );
}

export default Sudowrite;
